import { ERolesCode } from "@/shared/models/enums/roles/roles-code.enum";
import { ERolesId } from "@/shared/models/enums/roles/roles-id.enum";
import { useLoggedUser } from "@/shared/store/auth";

/**
 * Hook that provides various permission-related functions based on the logged in user's role.
 *
 * @function useUserHasPermission
 * @returns {{
 *  userIs: (...arg: ERolesCode[]) => boolean,
 *  userIsById: (...arg: ERolesId[]) => boolean,
 *  userIsNotById: (...arg: ERolesId[]) => boolean,
 *  userIsNot: (...arg: ERolesCode[]) => boolean,
 *  userIsAdminFromThatAgency: (agency_id: number) => boolean,
 *  userIshimself: (people_id: number) => boolean
 * }} - An object with permission-related functions.
 */
function useUserIs() {
  const { userLogged } = useLoggedUser();

  /**
   * Check if the user has a role that matches any of the given role codes.
   *
   * @function userIs
   * @param {...ERolesCode} arg - The role codes to check against.
   * @returns {boolean} - True if the user has a matching role code, false otherwise.
   * @example
   * const isAdmin = userIs(ERolesCode.ADMIN); // devuelve true si el usuario es un ADMIN
   */
  const userIs = (...arg: ERolesCode[]) => {
    if (typeof userLogged?.userData !== "undefined") {
      return arg.includes(
        userLogged?.userData?.user_type_code?.code as any
      );
    }
    return false;
  };
  /**
   * Check if the user has a role that matches any of the given role IDs.
   *
   * @function userIsById
   * @param {...ERolesId} arg - The role IDs to check against.
   * @returns {boolean} - True if the user has a matching role ID, false otherwise.
   *  @example
   * const isManager = userIsById(ERolesId.MANAGER); // devuelve true si el usuario es un MANAGER por su ID
   */
  const userIsById = (...arg: ERolesId[]) => {
    if (typeof userLogged?.userData !== "undefined") {
      return arg.includes(userLogged.userData?.user_type_code.id);
    }
    return false;
  };

  /**
   * Check if the user does not have a role that matches any of the given role codes.
   *
   * @function userIsNot
   * @param {...ERolesCode} arg - The role codes to check against.
   * @returns {boolean} - True if the user does not have a matching role code, false otherwise.
   * @example
   * const isNotUser = userIsNot(ERolesCode.USER); // devuelve true si el usuario NO es un USER
   */
  const userIsNot = (...arg: ERolesCode[]) => {
    if (typeof userLogged?.userData !== "undefined") {
      return !arg.includes(userLogged.userData?.user_type_code.code);
    }
    return false;
  };

  /**
   * Check if the user does not have a role that matches any of the given role IDs.
   *
   * @function userIsNotById
   * @param {...ERolesId} arg - The role IDs to check against.
   * @returns {boolean} - True if the user does not have a matching role ID, false otherwise.
   *  @example
   * const isNotSupervisor = userIsNotById(ERolesId.SUPERVISOR); // devuelve true si el usuario NO es un SUPERVISOR por su ID
   */
  const userIsNotById = (...arg: ERolesId[]) => {
    if (typeof userLogged?.userData !== "undefined") {
      return !arg.includes(userLogged.userData?.user_type_code.id);
    }
    return false;
  };

  /**
   * Check if the user logged is a agent.
   *
   * @function userIsNotById
   * @returns {boolean} - True if the user does not have a matching role ID, false otherwise.
   *  @example
   */
  const userIsAgent = () => {
    if (typeof userLogged?.userData !== "undefined") {
      return [
        ERolesCode.BROKER,
        ERolesCode.SENIOR,
        ERolesCode.JUNIOR,
        ERolesCode.BROKER_MANAGER
      ].includes(userLogged.userData?.user_type_code.code);
    }
    return false;
  };

  /**
   * Check if the user logged is a agent.
   *
   * @function userIsNotById
   * @returns {boolean} - True if the user does not have a matching role ID, false otherwise.
   *  @example
   */
  const isUserTypeAdministrator = () => {
    if (typeof userLogged?.userData !== "undefined") {
      return [ERolesCode.SUPER_ADMIN, ERolesCode.ADMIN].includes(
        userLogged.userData?.user_type_code.code
      );
    }
    return false;
  };

  /**
   * Check if the user is an admin for the given agency ID.
   *
   * @function userIsAdminFromThatAgency
   * @param {number} agency_id - The agency ID to check against.
   * @returns {boolean} - True if the user is an admin for the given agency
   * @example
   * const isAdminOfAgency = userIsAdminFromThatAgency(123); // devuelve true si el usuario es un ADMIN de la agencia con ID 123
   */
  const userIsAdminFromThatAgency = (agency_id: number) => {
    if (typeof userLogged?.userData !== "undefined") {
      if (userLogged.userData.user_type_code.id === ERolesId.ADMIN) {
        return userLogged.userData.agency_id === agency_id;
      } else {
        return false;
      }
    }
    return false;
  };

  const userIsHimSelf = (people_id: number) => {
    if (typeof userLogged?.userData !== "undefined") {
      return userLogged.userData?.people_id === people_id;
    }
    return false;
  };

  return {
    userIs,
    userIsById,
    userIsNotById,
    userIsNot,
    userIsAdminFromThatAgency,
    userIsHimSelf,
    userIsAgent,
    isUserTypeAdministrator,
    userLogged
  };
}

export default useUserIs;
